import React, { useContext, useEffect, useMemo, useState } from "react";
import {
  Box,
  Grid,
  TextField,
  Typography,
  Switch,
  FormControlLabel,
  MenuItem,
  Paper,
  Chip,
  Stack,
  Button,
  IconButton,
  Card,
  CardMedia,
  Dialog,
  DialogContent,
  DialogActions,
  Autocomplete
} from "@mui/material";
import AddPhotoAlternateIcon from "@mui/icons-material/AddPhotoAlternate";
import CloseIcon from "@mui/icons-material/Close";
import { AppContext } from "../../../../context/AppContext";

const IVA_OPTIONS = [
  { value: 0, label: "0%" },
  { value: 5, label: "5%" },
  { value: 15, label: "15%" },
  { value: 999, label: "No objeto de IVA" }
];

const ProductInfoTab = ({
  product = {},
  formData = {},
  setFormData,
  existingImages = [],
  setExistingImages,
  newImages = [],
  setNewImages
}) => {
  const { accessToken } = useContext(AppContext);
  const [openImage, setOpenImage] = useState(null);
  const [sections, setSections] = useState([]);
  const [categories, setCategories] = useState([]);
  const [brands, setBrands] = useState([]);

  const authFetch = (url) => {
    const headers = accessToken ? { Authorization: `Bearer ${accessToken}` } : {};
    return fetch(url, {
      credentials: "include",
      headers
    });
  };

  const sortByName = (arr) => {
    return [...arr].sort((a, b) =>
      String(a?.name || "").localeCompare(String(b?.name || ""), "es", {
        sensitivity: "base"
      })
    );
  };

  const fetchSections = async () => {
    try {
      const response = await authFetch("/categorias/secciones/");
      const data = await response.json().catch(() => []);
      if (response.ok) setSections(sortByName(Array.isArray(data) ? data : []));
    } catch {
      setSections([]);
    }
  };

  const fetchBrands = async () => {
    try {
      const response = await authFetch("/categorias/marcas/");
      const data = await response.json().catch(() => []);
      if (response.ok) setBrands(sortByName(Array.isArray(data) ? data : []));
    } catch {
      setBrands([]);
    }
  };

  const fetchCategoriesBySection = async (sectionId) => {
    if (!sectionId) {
      setCategories([]);
      return;
    }

    try {
      const response = await authFetch(`/categorias/seccion/${sectionId}/`);
      const data = await response.json().catch(() => []);
      if (response.ok) setCategories(sortByName(Array.isArray(data) ? data : []));
    } catch {
      setCategories([]);
    }
  };

  useEffect(() => {
    fetchSections();
    fetchBrands();
  }, [accessToken]);

  useEffect(() => {
    fetchCategoriesBySection(formData.section);
  }, [formData.section, accessToken]);

  const updateField = (field, value) => {
    setFormData((prev) => {
      const next = {
        ...prev,
        [field]: value
      };

      const pvp = parseFloat(String(field === "pvp1" ? value : next.pvp1 || "0").replace(",", "."));
      const ivaPct = Number(field === "iva_porcentaje" ? value : next.iva_porcentaje || 0);
      const iva = ivaPct === 999 ? 0 : pvp * (ivaPct / 100);

      next.iva_valor = Number.isFinite(iva) ? iva.toFixed(6) : "0.000000";
      next.precio_final = Number.isFinite(pvp + iva) ? (pvp + iva).toFixed(6) : "0.000000";

      return next;
    });
  };

  const updateSection = (value) => {
    setFormData((prev) => {
      const pvp = parseFloat(String(prev.pvp1 || "0").replace(",", "."));
      const ivaPct = Number(prev.iva_porcentaje || 0);
      const iva = ivaPct === 999 ? 0 : pvp * (ivaPct / 100);

      return {
        ...prev,
        section: value,
        category: "",
        iva_valor: Number.isFinite(iva) ? iva.toFixed(6) : "0.000000",
        precio_final: Number.isFinite(pvp + iva) ? (pvp + iva).toFixed(6) : "0.000000"
      };
    });
  };

  const selectedSection = useMemo(() => {
    return sections.find((item) => String(item.id) === String(formData.section)) || null;
  }, [sections, formData.section]);

  const selectedCategory = useMemo(() => {
    return categories.find((item) => String(item.id) === String(formData.category)) || null;
  }, [categories, formData.category]);

  const selectedBrand = useMemo(() => {
    return brands.find((item) => String(item.id) === String(formData.brand)) || null;
  }, [brands, formData.brand]);

  const handleAddImages = (event) => {
    const files = Array.from(event.target.files || []);
    const mapped = files.map((file) => ({
      file,
      preview: URL.createObjectURL(file)
    }));

    setNewImages((prev) => [...prev, ...mapped]);
    event.target.value = "";
  };

  const removeExistingImage = (index) => {
    setExistingImages((prev) => prev.filter((_, i) => i !== index));
  };

  const removeNewImage = (index) => {
    setNewImages((prev) => {
      const item = prev[index];
      if (item?.preview) URL.revokeObjectURL(item.preview);
      return prev.filter((_, i) => i !== index);
    });
  };

  const imageCards = [
    ...existingImages.map((img, index) => ({
      type: "existing",
      key: img?.key || `existing-${index}-${img?.url}`,
      url: img?.url,
      index
    })),
    ...newImages.map((img, index) => ({
      type: "new",
      key: `new-${index}-${img?.preview}`,
      url: img?.preview,
      index
    }))
  ].filter((img) => img.url);

  return (
    <Box>
      <Grid container spacing={3}>
        <Grid item xs={12} md={8}>
          <Stack spacing={3}>
            <Paper sx={{ p: 3, borderRadius: 3, border: "1px solid #e2e8f0" }}>
              <Typography sx={{ fontWeight: 800, color: "#0f172a", mb: 2 }}>
                Información editable
              </Typography>

              <Grid container spacing={2}>
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    required
                    label="Nombre del producto"
                    value={formData.name || ""}
                    onChange={(e) => updateField("name", e.target.value)}
                  />
                </Grid>

                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    label="Código"
                    value={product?.code || ""}
                    InputProps={{ readOnly: true }}
                  />
                </Grid>

                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    label="Código de barras"
                    value={formData.barcode || ""}
                    onChange={(e) => updateField("barcode", e.target.value)}
                  />
                </Grid>

                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    label="Código auxiliar"
                    value={formData.codigo_auxiliar || ""}
                    onChange={(e) => updateField("codigo_auxiliar", e.target.value)}
                  />
                </Grid>

                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    label="Stock mínimo"
                    type="number"
                    value={formData.stock_minimo || ""}
                    onChange={(e) => updateField("stock_minimo", e.target.value)}
                  />
                </Grid>

                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    required
                    label="PVP1"
                    value={formData.pvp1 || ""}
                    onChange={(e) => updateField("pvp1", e.target.value.replace(/,/g, "."))}
                  />
                </Grid>

                <Grid item xs={12} md={6}>
                  <TextField
                    select
                    fullWidth
                    label="IVA %"
                    value={formData.iva_porcentaje ?? 15}
                    onChange={(e) => updateField("iva_porcentaje", parseInt(e.target.value, 10))}
                  >
                    {IVA_OPTIONS.map((option) => (
                      <MenuItem key={option.value} value={option.value}>
                        {option.label}
                      </MenuItem>
                    ))}
                  </TextField>
                </Grid>

                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    label="IVA $"
                    value={formData.iva_valor || ""}
                    InputProps={{ readOnly: true }}
                  />
                </Grid>

                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    label="Precio final"
                    value={formData.precio_final || ""}
                    InputProps={{ readOnly: true }}
                  />
                </Grid>

                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    multiline
                    rows={6}
                    label="Descripción"
                    value={formData.description || ""}
                    onChange={(e) => updateField("description", e.target.value)}
                  />
                </Grid>
              </Grid>
            </Paper>

            <Paper sx={{ p: 3, borderRadius: 3, border: "1px solid #e2e8f0" }}>
              <Box
                sx={{
                  display: "flex",
                  alignItems: { xs: "flex-start", sm: "center" },
                  justifyContent: "space-between",
                  gap: 2,
                  flexWrap: "wrap",
                  mb: 2
                }}
              >
                <Box>
                  <Typography sx={{ fontWeight: 800, color: "#0f172a" }}>
                    Imágenes del producto
                  </Typography>
                  <Typography sx={{ color: "#64748b", fontSize: 13, mt: 0.4 }}>
                    Puedes agregar nuevas imágenes o quitar las actuales antes de actualizar
                  </Typography>
                </Box>

                <Button
                  variant="contained"
                  component="label"
                  startIcon={<AddPhotoAlternateIcon />}
                  sx={{
                    backgroundColor: "#1565C0",
                    "&:hover": { backgroundColor: "#0d47a1" },
                    textTransform: "none",
                    boxShadow: 3,
                    borderRadius: 2,
                    fontWeight: 700
                  }}
                >
                  Agregar imágenes
                  <input type="file" hidden accept="image/*" multiple onChange={handleAddImages} />
                </Button>
              </Box>

              {imageCards.length === 0 ? (
                <Box
                  sx={{
                    border: "1px dashed #cbd5e1",
                    borderRadius: 3,
                    p: 4,
                    textAlign: "center",
                    color: "#64748b"
                  }}
                >
                  <Typography sx={{ fontWeight: 700 }}>
                    Este producto no tiene imágenes
                  </Typography>
                  <Typography sx={{ fontSize: 13, mt: 0.5 }}>
                    Agrega una o más imágenes y luego presiona Actualizar producto
                  </Typography>
                </Box>
              ) : (
                <Grid container spacing={2}>
                  {imageCards.map((img) => (
                    <Grid item xs={6} sm={4} md={3} key={img.key}>
                      <Card
                        sx={{
                          width: "100%",
                          paddingTop: "100%",
                          position: "relative",
                          borderRadius: 2,
                          boxShadow: 4,
                          overflow: "hidden",
                          cursor: "pointer",
                          "&:hover": { boxShadow: 6 }
                        }}
                        onClick={() => setOpenImage(img.url)}
                      >
                        <CardMedia
                          component="img"
                          image={img.url}
                          alt="Imagen del producto"
                          sx={{
                            position: "absolute",
                            top: 0,
                            left: 0,
                            width: "100%",
                            height: "100%",
                            objectFit: "cover"
                          }}
                        />

                        <IconButton
                          onClick={(e) => {
                            e.stopPropagation();
                            if (img.type === "existing") removeExistingImage(img.index);
                            else removeNewImage(img.index);
                          }}
                          size="small"
                          sx={{
                            position: "absolute",
                            top: 6,
                            right: 6,
                            backgroundColor: "rgba(0, 0, 0, 0.6)",
                            color: "white",
                            padding: "4px",
                            zIndex: 1,
                            "&:hover": { backgroundColor: "rgba(0, 0, 0, 0.8)" }
                          }}
                        >
                          <CloseIcon fontSize="small" />
                        </IconButton>
                      </Card>
                    </Grid>
                  ))}
                </Grid>
              )}
            </Paper>
          </Stack>
        </Grid>

        <Grid item xs={12} md={4}>
          <Stack spacing={3}>
            <Paper sx={{ p: 3, borderRadius: 3, border: "1px solid #e2e8f0" }}>
              <Typography sx={{ fontWeight: 800, color: "#0f172a", mb: 2 }}>
                Estado del producto
              </Typography>

              <Stack spacing={1.5}>
                <FormControlLabel
                  control={
                    <Switch
                      checked={Boolean(formData.seriado)}
                      onChange={(e) => updateField("seriado", e.target.checked)}
                    />
                  }
                  label="Producto seriado"
                />

                <FormControlLabel
                  control={
                    <Switch
                      checked={Boolean(formData.estado)}
                      onChange={(e) => updateField("estado", e.target.checked)}
                    />
                  }
                  label={formData.estado ? "Activo" : "Inactivo"}
                />

                <FormControlLabel
                  control={
                    <Switch
                      checked={Boolean(formData.ecommerce)}
                      onChange={(e) => updateField("ecommerce", e.target.checked)}
                    />
                  }
                  label={formData.ecommerce ? "Visible en e-commerce" : "No visible en e-commerce"}
                />
              </Stack>
            </Paper>

            <Paper sx={{ p: 3, borderRadius: 3, border: "1px solid #e2e8f0" }}>
              <Typography sx={{ fontWeight: 800, color: "#0f172a", mb: 2 }}>
                Clasificación editable
              </Typography>

              <Stack spacing={2}>
                <Autocomplete
                  options={sections}
                  value={selectedSection}
                  onChange={(_, value) => updateSection(value ? String(value.id) : "")}
                  getOptionLabel={(option) => option?.name || ""}
                  isOptionEqualToValue={(option, value) => String(option?.id) === String(value?.id)}
                  renderInput={(params) => <TextField {...params} fullWidth label="Sección" />}
                />

                <Autocomplete
                  options={categories}
                  value={selectedCategory}
                  onChange={(_, value) => updateField("category", value ? String(value.id) : "")}
                  getOptionLabel={(option) => option?.name || ""}
                  isOptionEqualToValue={(option, value) => String(option?.id) === String(value?.id)}
                  renderInput={(params) => <TextField {...params} fullWidth label="Categoría" />}
                  disabled={!formData.section}
                />

                <Autocomplete
                  options={brands}
                  value={selectedBrand}
                  onChange={(_, value) => updateField("brand", value ? String(value.id) : "")}
                  getOptionLabel={(option) => option?.name || ""}
                  isOptionEqualToValue={(option, value) => String(option?.id) === String(value?.id)}
                  renderInput={(params) => <TextField {...params} fullWidth label="Marca" />}
                />
              </Stack>
            </Paper>

            <Paper sx={{ p: 3, borderRadius: 3, border: "1px solid #e2e8f0" }}>
              <Typography sx={{ fontWeight: 800, color: "#0f172a", mb: 2 }}>
                Variantes
              </Typography>

              <Stack spacing={1}>
                <Chip
                  label={product?.has_variants ? "Producto con variantes" : "Producto sin variantes"}
                  color={product?.has_variants ? "info" : "default"}
                  variant={product?.has_variants ? "filled" : "outlined"}
                  sx={{ fontWeight: 700, borderRadius: 2 }}
                />

                {product?.has_variants && (
                  <Chip
                    label={product?.variant_type ? `Tipo: ${product.variant_type}` : "Tipo no definido"}
                    color="info"
                    variant="outlined"
                    sx={{ fontWeight: 700, borderRadius: 2 }}
                  />
                )}
              </Stack>
            </Paper>
          </Stack>
        </Grid>
      </Grid>

      <Dialog open={Boolean(openImage)} onClose={() => setOpenImage(null)} maxWidth="md" fullWidth>
        <DialogActions sx={{ p: 1 }}>
          <IconButton onClick={() => setOpenImage(null)} sx={{ ml: "auto" }}>
            <CloseIcon fontSize="small" />
          </IconButton>
        </DialogActions>
        <DialogContent
          sx={{
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            p: 2
          }}
        >
          <Box
            component="img"
            src={openImage}
            alt="Vista previa"
            sx={{
              width: "100%",
              maxHeight: "600px",
              objectFit: "contain",
              borderRadius: 2,
              boxShadow: 3
            }}
          />
        </DialogContent>
      </Dialog>
    </Box>
  );
};

export default ProductInfoTab;